import { FontMonitoringContextProps } from "contexts-types";
import { Columns, Props } from "props";
import React, { createContext, useContext, useEffect } from "react";
import { useState } from "react";
import {
  IFontStatusHistoryViewModel,
  IFontTestViewModel,
  IHistoryTestDataViewModel,
} from "viewModels";
import { useAuth } from "./auth";
import { useInfiniteQuery } from "react-query";

import { FontMonitoringController } from "controllers/FontMonitoringController";
import useWebSocket from "hooks/useWebSocket";
import dayjs from "dayjs";
import { FontTestStatus, TypeUser } from "types/enums";

const PAGE_SIZE = 10;
const FontMonitoringContext = createContext<FontMonitoringContextProps>(
  {} as FontMonitoringContextProps
);

const FontMonitoringProvider: React.FC<Props> = ({ children }) => {
  const { user, logged } = useAuth();
  const [reports, setReports] = useState<IFontTestViewModel[]>([]);
  const [fontStatusHistory, setFontStatusHistory] = useState<
    IFontStatusHistoryViewModel[]
  >([]);
  const [testHistory, setTestHistory] = useState<IHistoryTestDataViewModel[]>(
    []
  );
  const [isLoadingTestHistory, setIsLoadingTestHistory] =
    useState<boolean>(false);
  const [isLoadingFontsHistory, setIsLoadingFontsHistory] =
    useState<boolean>(false);

  const [startFontDate, setStartFontDate] = useState<dayjs.Dayjs>(
    dayjs().subtract(1, "month")
  );

  const [endFontDate, setEndFontDate] = useState<dayjs.Dayjs>(
    dayjs().endOf("day")
  );

  const [startHistoryDate, setStartHistoryDate] = useState<dayjs.Dayjs>(
    dayjs().subtract(1, "month")
  );

  const [endHistoryDate, setEndHistoryDate] = useState<dayjs.Dayjs>(
    dayjs().endOf("day")
  );
  const {
    data: results,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["fonts-report"],
    ({ pageParam = 1 }) =>
      FontMonitoringController.getReport<Columns>({
        page: pageParam,
        take: PAGE_SIZE,
      }).then((res) => res.data),
    {
      enabled: !!user?.id && logged && user.typeUser === TypeUser.ADM,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    }
  );

  const handleFetchNextPage = () => {
    fetchNextPage();
  };

  const handleFetch = () => {
    refetch();
  };

  const handleCancelReport = (reportId: string) => {
    FontMonitoringController.cancelReport(reportId).then(() => {
      setReports((prev) => {
        const existingReportIndex = prev.findIndex((t) => t.id === reportId);
        if (existingReportIndex !== -1) {
          const updatedReports = [...prev];
          updatedReports[existingReportIndex].status = FontTestStatus.CANCELED;
          return updatedReports;
        } else {
          return prev;
        }
      });
    });
  };

  useWebSocket<IFontTestViewModel>({
    url: `font-report/${user?.id}`,
    onMessage: (report) => {
      setReports((prev) => {
        const existingReportIndex = prev.findIndex((t) => t.id === report.id);
        if (existingReportIndex !== -1) {
          const updatedReports = [...prev];
          updatedReports[existingReportIndex] = report;
          return updatedReports;
        } else {
          return [report, ...prev];
        }
      });
    },
  });

  useEffect(() => {
    setReports(results?.pages?.flat() ?? []);
  }, [results, setReports]);

  useEffect(() => {
    if (!!user?.id && logged && user.typeUser === TypeUser.ADM) {
      setIsLoadingFontsHistory(true);
      FontMonitoringController.getFontsHistory(startFontDate, endFontDate)
        .then((response) => {
          setFontStatusHistory(response.data);
        })
        .finally(() => setIsLoadingFontsHistory(false));
    }
  }, [user, logged, startFontDate, endFontDate]);

  useEffect(() => {
    if (!!user?.id && logged && user.typeUser === TypeUser.ADM) {
      setIsLoadingTestHistory(true);
      FontMonitoringController.getReportHistory(
        startHistoryDate,
        endHistoryDate
      )
        .then((response) => {
          setTestHistory(response.data);
        })
        .finally(() => setIsLoadingTestHistory(false));
    }
  }, [user, logged, startHistoryDate, endHistoryDate]);

  return (
    <FontMonitoringContext.Provider
      value={{
        reports,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        handleFetchNextPage,
        handleFetch,
        fontStatusHistory,
        testHistory,
        isLoadingTestHistory,
        isLoadingFontsHistory,
        startHistoryDate,
        endHistoryDate,
        setEndHistoryDate,
        setStartHistoryDate,
        startFontDate,
        endFontDate,
        setEndFontDate,
        setStartFontDate,
        handleCancelReport,
      }}
    >
      {children}
    </FontMonitoringContext.Provider>
  );
};

export const useFontMonitoring: () => FontMonitoringContextProps = () =>
  useContext(FontMonitoringContext);

export default FontMonitoringProvider;
